import React, { useState, useEffect } from 'react';
import LineSteps from '@gumtree/ui-library/src/line-steps/line-steps';
import Icon from '@gumtree/ui-library/src/icon/icon';
import ClearButton, { useHandleClearButton } from '@gumtree/ui-library/src/input-clear-button/input-clear-button';
import { ModalSearchBarWrapper, ModalSearchBar, SearchCancelButton, NoResultsScreen } from '@gumtree/ui-library/src/select-modal/select-modal.style';
import MultiSelectSearchResult from '@gumtree/ui-library/src/multi-select-search-result/multi-select-search-result';
import { MultiSelectData } from '@gumtree/ui-library/src/multi-select-modal/multi-select-modal';
import { qaAttribute } from '@gumtree/ui-library/src/utils/qa-service';
import debounce from '@gumtree/ui-library/src/utils/debounce';
import { trackGA4Event } from '@gumtree/shared/src/util/ga4-shared';
import { ContentWrapper, PostAJobDialog } from './index.style';
import DialogHeader from './dialog-header';
import ServiceForm from './service-form';
import DescribeForm from './describe-form';
import ContactForm from './contact-form';
import { QaItem } from '../../types';
import { getIsRemote, getSellerSkills, submitLeadForm } from '../../get-data/fetch/fetch-post-a-job';
import { useFormCache } from '../../hooks/useFormCache';
import { useFormHandlers } from '../../hooks/useFormHandlers';
import { buildBaseSubmitData } from '../../utils/formUtils';

interface Props {
    isModalOpen: boolean;
    onCloseModal: () => void;
    onSubmitSuccess: (message: string) => void;
    onSubmitFail: (message: string) => void;
    qaList: QaItem[];
    selectedAnswerId: string;
    userId: string;
    categoryId: string;
    currentPageUrl: string;
    requestId: string;
    buyerUrl: string;
    app?: boolean;
}

export interface DescribeFormData {
    jobDescription?: string;
    postcode?: string;
    qaList?: string;
    remote?: string;
}

export interface ContactFormData {
    buyerName?: string;
    buyerEmail?: string;
    phoneNumber?: string;
    isMoreProviders?: boolean;
}

interface SellerSkill {
    id: number;
    description: string;
}

const PostAJobForm: React.FC<Props> = ({
    isModalOpen,
    onCloseModal,
    onSubmitSuccess,
    onSubmitFail,
    qaList,
    selectedAnswerId,
    userId,
    categoryId,
    currentPageUrl,
    requestId,
    buyerUrl,
    app = false
}) => {
    const [loading, setLoading] = useState(false);
    const [searchStatus, setSearchStatus] = useState(false);
    const [noResults, setNoResults] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredData, setFilteredData] = useState<MultiSelectData[]>([]);
    const [nationalWide, setNationalWide] = useState('');

    const {
        currentStep,
        setCurrentStep,
        selectedService,
        setSelectedService,
        describeFormData,
        setDescribeFormData,
        contactFormData,
        setContactFormData
    } = useFormCache<MultiSelectData>(app);

    const { handleDescribeFormDataChange, handleContactFormDataChange } = useFormHandlers(
        setDescribeFormData,
        setContactFormData
    );

    const elName = 'post-a-job-form';
    
    const { inputRef, showClear, onClickClear } = useHandleClearButton('service');

    useEffect(() => {
        if (isModalOpen) {
            trackGA4Event<any>({
                event: 'form_show',
                formName: 'bark lead',
                formStep: 'select service',
                linkText: 'post a job',
                requestId,
                listingId: ''
            });
        }
    }, [isModalOpen]);

    useEffect(() => {
        if (searchStatus && inputRef.current) {
            setTimeout(() => {
                inputRef.current?.focus();
            }, 0);
        }
    }, [searchStatus]);

    useEffect(() => {
        if (selectedAnswerId && !describeFormData.qaList) {
            setDescribeFormData(prev => ({
                ...prev,
                qaList: selectedAnswerId
            }));
        }
    }, [selectedAnswerId, describeFormData.qaList]);

    const debouncedSearch = debounce(async (keyword: string) => {
        if (keyword.length >= 2) {
            try {
                const params = {
                    keyword,
                    categoryIds: [categoryId],
                    adSource: 'Bark'
                };

                const sellerSkills: SellerSkill[] = await getSellerSkills(buyerUrl, params);
                const transformedData: MultiSelectData[] = sellerSkills.map((skill) => ({
                    id: skill.id,
                    displayName: skill.description
                }));
                
                setFilteredData(transformedData);
                setNoResults(transformedData.length === 0);
            } catch (error) {
                console.error('Error fetching seller skills:', error);
                setFilteredData([]);
                setNoResults(true);
            }
        } else {
            setFilteredData([]);
            setNoResults(false);
        }
    }, 300);

    const goToStep = (step: number) => {
        setCurrentStep(step);
    };

    const handleServiceContinue = async () => {
        const isRemoteData = await getIsRemote(Number(selectedService?.id));
        setNationalWide(isRemoteData);
        goToStep(2);
    };

    const handleDescribeBack = () => {
        goToStep(1);
    };

    const handleDescribeNext = () => {
        goToStep(3);
    };

    const handleContactBack = () => {
        goToStep(2);
    };

    const trackFormSubmission = (isSuccess: boolean) => {
        trackGA4Event<GA4.SubmitForm>({
            event: 'form_submit',
            formName: 'bark lead',
            formStep: isSuccess ? 'success' : 'failed',
            formValidation: isSuccess ? 'success' : 'failure',
            linkText: 'post a job',
            requestId
        });
    };

    const handleSubmitForm = async () => {
        setLoading(true);
        try {
            const submitData = buildBaseSubmitData(
                describeFormData,
                contactFormData,
                qaList,
                requestId,
                userId,
                currentPageUrl,
                categoryId,
                selectedService,
                'post a job'
            );
            
            const result = await submitLeadForm(submitData);
            
            if (['00', '04'].includes(result?.code)) {
                trackFormSubmission(true);

                onSubmitSuccess('All done!\nYou\'ll hear back from up to 5 available providers within 72 hours.');
                // Reset all form data
                setSearchStatus(false);
                setCurrentStep(1);
                setNoResults(false);
                setSearchTerm('');
                setFilteredData([]);
                setSelectedService(null);
                setDescribeFormData({});
                setContactFormData({
                    isMoreProviders: true
                });
            } else {
                trackFormSubmission(false);
                
                setCurrentStep(1);
                // TODO: uncomment this when the text is updated
                // onSubmitFail(result?.msg || 'We had trouble submitting your form.\nPlease try again.');  
                onSubmitFail('We had trouble submitting your form.\nPlease try again.');
            }
        } catch (error) {
            trackFormSubmission(false);
            console.error('Error submitting form:', error);
            onSubmitFail('We had trouble submitting your form.\nPlease try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleSearchInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchTerm(value);
        debouncedSearch(value);
    };

    const handleClear = () => {
        onClickClear();
        setSearchTerm('');
        setFilteredData([]);
        setNoResults(false);
    };

    const handleSearchCancel = () => {
        setSearchStatus(false);
        inputRef.current?.blur();
        handleClear();
    };

    const handlePostAJobDialogClose = () => {
        setCurrentStep(1);
        onCloseModal();
    };

    const handleSearchResultToggle = (data: MultiSelectData) => {
        setSelectedService(data);
        setSearchStatus(false);
        inputRef.current?.blur();
        handleClear();
    };

    const modalHeader = () => {
        if (searchStatus) {
            return (
                <ModalSearchBarWrapper modalType="post-a-job">
                    <ModalSearchBar className="search-bar">
                        <Icon type="magnifying-glass" size="medium" />
                        <input
                            ref={inputRef}
                            type="text"
                            placeholder="Search for services..."
                            onChange={handleSearchInput}
                        />
                        {showClear && <ClearButton onClick={handleClear} />}
                    </ModalSearchBar>
                    <SearchCancelButton
                        className="search-button"
                        type="button"
                        onClick={handleSearchCancel}
                    >
                        Cancel
                    </SearchCancelButton>
                </ModalSearchBarWrapper>
            );
        }
        
        return <DialogHeader />;
    };

    return (
        <PostAJobDialog
            isOpen={isModalOpen}
            searchStatus={searchStatus}
            hideCloseBtn={app || searchStatus}
            onClose={handlePostAJobDialogClose}
            title={modalHeader()}
            displayAsSheetOnMweb
            app={app}
        >
            {!searchStatus ? (
                <>
                    <LineSteps total={3} current={currentStep} />
                    {currentStep === 1 && (
                        <ServiceForm 
                            selectedService={selectedService?.displayName || ''} 
                            onSearchClick={() => setSearchStatus(true)}
                            onContinue={handleServiceContinue}
                        />
                    )}
                    {currentStep === 2 && (
                        <DescribeForm 
                            subDescription="Add details about your job to get quotes from providers that can meet your schedule and budget."
                            onBack={handleDescribeBack}
                            onNext={handleDescribeNext}
                            formData={describeFormData}
                            onFormDataChange={handleDescribeFormDataChange}
                            qaList={qaList}
                            nationalWide={nationalWide}
                            app={app}
                        />
                    )}
                    {currentStep === 3 && (
                        <ContactForm 
                            onBack={handleContactBack}
                            formData={contactFormData}
                            onFormDataChange={handleContactFormDataChange}
                            onSubmit={handleSubmitForm}
                            loading={loading}
                            app={app}
                        />
                    )}
                </>
            ) : noResults ? (
                <ContentWrapper>
                    <NoResultsScreen {...qaAttribute('no-results-screen')}>
                        <p>
                            No search results for <br /> '{searchTerm}'
                        </p>
                    </NoResultsScreen>
                </ContentWrapper>
            ) : (
                <ContentWrapper>
                    <MultiSelectSearchResult
                        data={filteredData}
                        elName={elName}
                        searchTerm={searchTerm}
                        handleSearchResultToggle={handleSearchResultToggle}
                    />
                </ContentWrapper>
            )}
        </PostAJobDialog>
    );
}

export default PostAJobForm;
